import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../components/Button'
import { Checkbox } from '../components/Checkbox'
import { FormNavigation } from '../components/FormNavigation'
import { InfoBox } from '../components/InfoBox'
import { Download, Loader2, FileText } from 'lucide-react'
import { yousignService, getApiErrorCode } from '../services/api'
import { useOnboardingStore } from '../store/onboardingStore'
import { useTestMode } from '../context/TestModeContext'
import { toast } from 'sonner'
import { logger } from '../utils/logger'
import type { StepProps } from '../types'
import type { ContractConfig } from '../context/TenantContext'

interface ContractSigningStepProps extends StepProps {
  contract: ContractConfig
}

export function ContractSigningStep({ contract, onNext, onBack }: ContractSigningStepProps) {
  const { t, i18n } = useTranslation()
  const isTestMode = useTestMode()
  const [agreed, setAgreed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  // Inline preview: the treatment contract was the #2 abandonment point
  // (p90 18 min) — patients had to download a PDF, open it elsewhere, come
  // back and tick a box. Now the document is on the page itself.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewFailed, setPreviewFailed] = useState(false)
  const { personalData, setConsent, setPtConfirmation, yousignRequestId, yousignDocuments, yousignDocumentMap } = useOnboardingStore()
  // A request that mixes template and uploaded documents (psychotherapy variant) names each
  // document; otherwise the contract's position in the org template finds it
  const documentId = yousignDocumentMap?.[contract.key] ?? yousignDocuments[contract.documentIndex]?.id

  const lang = i18n.language
  const title = contract.title[lang] || contract.title['en'] || contract.title['de'] || contract.key
  const description = contract.description[lang] || contract.description['en'] || contract.description['de'] || ''
  const downloadLabel = contract.downloadLabel[lang] || contract.downloadLabel['en'] || contract.downloadLabel['de'] || t('common.download')

  useEffect(() => {
    if (!yousignRequestId || !documentId) return
    let url: string | null = null
    let cancelled = false
    yousignService.downloadDocument(yousignRequestId, documentId)
      .then(blob => {
        if (cancelled) return
        url = window.URL.createObjectURL(blob)
        setPreviewUrl(url)
      })
      .catch(err => { logger.warn('[Contract] inline preview unavailable:', err); setPreviewFailed(true) })
    return () => { cancelled = true; if (url) window.URL.revokeObjectURL(url) }
  }, [yousignRequestId, documentId])

  const handleDownload = async () => {
    if (!yousignRequestId || !documentId) {
      toast.info(t('common.documentNotAvailable'))
      return
    }

    setIsDownloading(true)
    try {
      const blob = await yousignService.downloadDocument(yousignRequestId, documentId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = contract.downloadFilename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success(`${title} ${t('step6.downloaded')}`)
    } catch (error) {
      // The document simply isn't signed/ready yet — not a failure to apologise for
      if (getApiErrorCode(error) === 'document_not_ready') {
        logger.warn('[Contract] document not ready yet')
        toast.info(t('common.documentNotAvailable'))
      } else {
        logger.error('Download failed:', error)
        toast.error(t('common.downloadFailed'))
      }
    } finally {
      setIsDownloading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!agreed) return

    setIsLoading(true)
    // Consent is recorded in the submission and evidenced by the signed PDFs —
    // no separate backend call needed here.
    if (['treatment_contract', 'privacy_policy', 'billing_consent'].includes(contract.consentType)) {
      setConsent(contract.consentType as 'treatment_contract' | 'privacy_policy' | 'billing_consent', true)
    } else {
      // Dok. 01 of the psychotherapy variant — evidenced by the signed PDF
      setPtConfirmation(contract.key, true)
    }
    toast.success(`${title} ✓`)
    setIsLoading(false)
    onNext()
  }

  const confirmText = {
    de: 'Hiermit bestätige ich, dass ich das Dokument gelesen und verstanden habe.',
    en: 'I hereby confirm that I have read and understood the document.',
    it: 'Confermo di aver letto e compreso il documento.',
    es: 'Confirmo que he leído y comprendido el documento.',
  }[lang] || 'I hereby confirm that I have read and understood the document.'

  const signatureText = {
    de: `Durch Klicken auf "Zustimmen" wird Ihr Name (${personalData.firstName} ${personalData.lastName}) als elektronische Signatur verwendet.`,
    en: `By clicking "Agree", your name (${personalData.firstName} ${personalData.lastName}) will be used as an electronic signature.`,
    it: `Cliccando su "Accetto", il vostro nome (${personalData.firstName} ${personalData.lastName}) verrà utilizzato come firma elettronica.`,
    es: `Al hacer clic en "Aceptar", su nombre (${personalData.firstName} ${personalData.lastName}) se utilizará como firma electrónica.`,
  }[lang] || `By clicking "Agree", your name (${personalData.firstName} ${personalData.lastName}) will be used as an electronic signature.`

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="form-inner py-12">
        <h2 className="text-h2 text-foreground mb-2">
          {title}
        </h2>
        <p className="text-body text-foreground mb-2">
          {personalData.firstName} {personalData.lastName}
        </p>
        <p className="text-body text-foreground mb-8">
          {description}
        </p>

        {previewUrl ? (
          <>
            {/* Desktop: read it right here, with an explicit open-in-tab
                affordance. Phones: iOS Safari renders a PDF inside an iframe
                as a single unscrollable page, so there the primary action
                opens the native viewer in a new tab. */}
            <div className="hidden md:block mb-4">
              <div className="flex items-center justify-between mb-1.5 text-small text-muted-foreground">
                <span>{t('common.previewLabel')}</span>
                <a href={previewUrl} target="_blank" rel="noopener" className="inline-flex items-center gap-1 text-primary underline">
                  <FileText className="w-3.5 h-3.5" />{t('common.openInNewTab')}
                </a>
              </div>
              <div className="rounded-lg border border-border overflow-hidden bg-white">
                <iframe
                  src={`${previewUrl}#toolbar=0&navpanes=0&view=FitH`}
                  title={title}
                  className="w-full h-[60vh] min-h-[420px]"
                />
              </div>
            </div>
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener"
              className="md:hidden mb-4 flex items-center justify-center gap-2 w-full rounded-lg border-2 border-primary bg-primary/5 px-4 py-3 text-body font-medium text-primary"
            >
              <FileText className="w-5 h-5" />{t('common.openDocument')}
            </a>
          </>
        ) : !previewFailed && yousignRequestId ? (
          <div className="mb-4 h-40 rounded-lg border border-border bg-white flex items-center justify-center text-muted-foreground text-small">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />{t('common.loading')}
          </div>
        ) : (
          // No signature request yet (test mode skips Yousign) or the
          // download failed — say so instead of showing nothing.
          <InfoBox className="mb-4">
            <p>{isTestMode ? t('common.previewUnavailableTest') : t('common.previewUnavailable')}</p>
          </InfoBox>
        )}

        <Button
          type="button"
          variant="download"
          onClick={handleDownload}
          disabled={isDownloading}
          className="w-full mb-8"
        >
          {isDownloading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Download className="w-5 h-5" />
          )}
          {isDownloading ? t('common.downloading') : downloadLabel}
        </Button>

        <p className="text-small text-muted-foreground mb-6 -mt-4">{t('common.previewHint')}</p>

        <Checkbox
          id={`agree-${contract.key}`}
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          label={confirmText}
        />

        <InfoBox className="mt-8">
          <p>{signatureText}</p>
        </InfoBox>

        <FormNavigation
          onBack={onBack}
          nextDisabled={!agreed || isLoading}
          nextLabel={isLoading ? t('common.loading') : t('common.agree')}
        />
      </form>
    </div>
  )
}
